import React, { useState } from 'react';
import styled from 'styled-components';
import { CategorySimple } from '../../../types';
import ImageUpload from '../../ImageUpload';
import { buildImageUrl } from '../../../services/api';

interface SiteSettings {
  primary_color: string;
  secondary_color: string;
  logo: string;
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  featured_categories: number[];
  [key: string]: any;
}

interface AppearanceSettingsProps {
  settings: SiteSettings;
  categories: CategorySimple[];
  onUpdate: (updates: Partial<SiteSettings>) => void;
}

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ColorInput = styled.input`
  width: 60px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
`;

const ColorGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ColorPreview = styled.div`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  font-family: monospace;
  font-size: 0.9rem;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CategoryItem = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : '#ddd'};
  border-radius: 4px;
  cursor: pointer;
  background: ${({ $selected }) => $selected ? '#f0f8ff' : 'white'};
  transition: all 0.3s;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  input {
    margin-right: 0.5rem;
  }
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const ImageUploadContainer = styled.div`
  margin-top: 0.5rem;
`;

const AppearanceSettings: React.FC<AppearanceSettingsProps> = ({ settings, categories, onUpdate }) => {
  const handleChange = (field: string, value: any) => {
    onUpdate({ [field]: value });
  };

  const handleLogoUpload = (url: string) => {
    handleChange('logo', url);
  };

  const handleHeroImageUpload = (url: string) => {
    handleChange('hero_image', url);
  };


  const handleCategoryToggle = (categoryId: number) => {
    const currentCategories = settings.featured_categories || [];
    const isSelected = currentCategories.includes(categoryId);
    
    let newCategories;
    if (isSelected) {
      newCategories = currentCategories.filter(id => id !== categoryId);
    } else {
      newCategories = [...currentCategories, categoryId];
    }
    
    handleChange('featured_categories', newCategories);
  };

  return (
    <>
      <Section>
        <SectionTitle>🎨 Cores e Identidade Visual</SectionTitle>
        
        <FormGroup>
          <Label>Cor Primária</Label>
          <ColorGroup>
            <ColorInput
              type="color"
              value={settings.primary_color}
              onChange={(e) => handleChange('primary_color', e.target.value)}
            />
            <ColorPreview>{settings.primary_color}</ColorPreview>
          </ColorGroup>
          <HelpText>Cor principal do site (botões, links, destaques)</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Cor Secundária</Label>
          <ColorGroup>
            <ColorInput
              type="color"
              value={settings.secondary_color}
              onChange={(e) => handleChange('secondary_color', e.target.value)}
            />
            <ColorPreview>{settings.secondary_color}</ColorPreview>
          </ColorGroup>
          <HelpText>Cor secundária para elementos de apoio</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="logo">Logo da Empresa</Label>
          <HelpText>Faça upload da imagem do logo da empresa</HelpText>
          
          <ImageUploadContainer>
            <ImageUpload
              onUpload={handleLogoUpload}
              id="logo-upload"
              currentImage={settings.logo}
            />
          </ImageUploadContainer>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>🏠 Seção Hero (Banner Principal)</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="hero_title">Título Principal</Label>
          <Input
            id="hero_title"
            type="text"
            value={settings.hero_title}
            onChange={(e) => handleChange('hero_title', e.target.value)}
            placeholder="Encontre o Imóvel dos Seus Sonhos"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="hero_subtitle">Subtítulo</Label>
          <TextArea
            id="hero_subtitle"
            value={settings.hero_subtitle}
            onChange={(e) => handleChange('hero_subtitle', e.target.value)}
            placeholder="Descrição atrativa sobre sua imobiliária..."
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="hero_image">Imagem de Fundo do Banner</Label>
          <HelpText>Faça upload da imagem de fundo do banner principal</HelpText>
          
          <ImageUploadContainer>
            <ImageUpload
              onUpload={handleHeroImageUpload}
              id="hero-upload"
              currentImage={settings.hero_image}
            />
          </ImageUploadContainer>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>⭐ Categorias em Destaque</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Selecione as categorias que aparecerão na página inicial
        </p>
        
        <CategoryGrid>
          {categories.map(category => (
            <CategoryItem
              key={category.id}
              $selected={settings.featured_categories?.includes(category.id) || false}
            >
              <input
                type="checkbox"
                checked={settings.featured_categories?.includes(category.id) || false}
                onChange={() => handleCategoryToggle(category.id)}
              />
              <span style={{ color: category.color }}>●</span>
              <span style={{ marginLeft: '0.5rem' }}>{category.name}</span>
            </CategoryItem>
          ))}
        </CategoryGrid>
      </Section>
    </>
  );
};

export default AppearanceSettings;